import { ArrowForward } from '@finn/design-system/icons/arrow-forward';
import { useIntl } from '@finn/ui-utils';
import Link from 'next/link';
import * as React from 'react';

import { TableOfContentsMetadata } from './TableOfContentsMetadata';
import { useAutoLinks } from './useAutoLinks';

type Props = {
  data: TableOfContentsMetadata;
};

const TableOfContents: React.FC<Props> = ({ data }) => {
  const i18n = useIntl();
  const autoLinks = useAutoLinks(!data.links.length);

  const links = data.links.length ? data.links : autoLinks;

  return (
    <nav>
      <div className="container sm:w-[calc(100%-2rem)] md:w-[calc(100%-1rem)] md:max-w-[726px]">
        <div className="mb-8">
          <h2 className="mobile-t4-semibold md:web-t4-semibold">
            {data.title}
          </h2>
        </div>

        {data.subtitle && (
          <div className="mb-4">
            <p className="body-16-regular">{data.subtitle}</p>
          </div>
        )}

        {/* Suppressing as per https://css-tricks.com/newer-things-to-know-about-good-ol-html-lists/#aa-newer-accessibility-concerns-with-lists */
        /* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
        <ul
          className="m-0 list-none p-0"
          role="list"
          aria-label={i18n.formatMessage('general.tableOfContentsAriaLabel')}
        >
          {links.map((link) => (
            <li key={link.url} className="mb-4 flex flex-nowrap items-center">
              <span
                className="mr-2 inline-block size-6 shrink-0 grow-0"
                aria-hidden="true"
              >
                <ArrowForward />
              </span>

              <Link shallow href={link.url}>
                <span className="grow underline body-16-semibold">
                  {link.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default React.memo(TableOfContents);
